import { Badge, Box, Card, CheckIcon, type ComboboxLikeRenderOptionInput, Group, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { type ChangeEvent, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FactTable } from "../../common/fact-table";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { UtilityTitle } from "../../common/utility-title";
import { NOWHERE, type Place, place, zoneWithOffset } from "./geo";
import { exampleNumber, type Reading, readNumber } from "./parse";
import { findRegion, pickRegion, REGION_OPTIONS, regionFilter, regionForInput, withRegion } from "./regions";
import type { Short } from "./short";
import { retype, retypeAll } from "./typing";

export default function PhoneNumber() {
  const initialState = useInitialHashState<{ number?: string; region?: string }>();

  const [input, setInput] = useState(() => typeof initialState?.number === "string" ? initialState.number : "");
  const [region, setRegion] = useState(() => pickRegion(initialState?.region));

  const inputRef = useRef<HTMLInputElement>(null);
  // Where the caret goes once the reformatted value has been written over the box, and null when nothing moved it.
  const caretRef = useRef<number | null>(null);

  // The region goes every time and is never left to the reader's own browser, for the reason /countries sends its
  // country: a link without it would read the digits against wherever the follower happens to be. The number is a
  // field somebody either filled in or did not, and an empty box is what the page does anyway.
  useRegisterShareState(() => ({ number: input || undefined, region: region.code }));

  const result = useMemo(() => readNumber(input, region), [input, region]);

  // Typing a plus hands the choice to the digits; picking a country writes that choice back into them. Either way
  // the flag and the number agree, which is the only state in which the picker means anything.
  const onInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.currentTarget;
    // The region the keystroke has just moved to, since formatting against the old one would lag it by a character.
    const named = regionForInput(value, region.code);
    const next = named ? pickRegion(named) : region;

    const typed = retype({ value, caret: selectionStart ?? value.length, previous: input }, next.code);
    setInput(typed.value);
    caretRef.current = typed.caret;
    if (next !== region) setRegion(next);
  };

  const onRegion = (code: string | null) => {
    const picked = pickRegion(code);
    setRegion(picked);
    // The digits are read as another country's now, and 0412345678 is spaced one way here and not at all there.
    setInput((current) => retypeAll(withRegion(current, picked), picked.code));
  };

  // React writes the reformatted value over the box, which puts the caret at its end; this puts it back where the
  // keystroke left it. It has to be a layout effect, or the caret is visibly at the end for a frame first.
  useLayoutEffect(() => {
    const caret = caretRef.current;
    caretRef.current = null;
    // Only ever for a box somebody is typing in: moving the selection of one nobody has focused would scroll it.
    if (caret === null || inputRef.current === null || document.activeElement !== inputRef.current) return;
    inputRef.current.setSelectionRange(caret, caret);
  });

  // Nothing typed yet is unfinished rather than wrong, per **Blank Is Not Wrong Until It Is Asked For** — and there
  // is no call to action here to ask, the page reading whatever is in the box as it is typed.
  const error = result.kind === "error" ? result.message : undefined;

  return (
    <Stack gap="md">
      <UtilityTitle directory="phone-number" publications="Google's libphonenumber">Phone Number</UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Box className={error ? "settings-row has-error" : "settings-row"} mb={error ? "md" : 0}>
          <Select
            label="Country"
            description="Searched by name, code or dialling code"
            data={REGION_OPTIONS}
            value={region.code}
            onChange={onRegion}
            filter={regionFilter}
            leftSection={<Text span>{region.flag}</Text>}
            renderOption={renderRegionOption}
            allowDeselect={false}
            searchable
            // Highlights the best match as the search changes, which also scrolls the list back up to it: the
            // dropdown opens on the region already selected, and filtering alone leaves it stranded down among 245.
            selectFirstOptionOnChange
            nothingFoundMessage="No country by that name"
          />
          <TextInput
            label="Phone number"
            description={`Dialling code +${region.callingCode}`}
            ref={inputRef}
            value={input}
            onChange={onInput}
            placeholder={exampleNumber(region)}
            error={error}
            classNames={{ root: "relative-root", error: "absolute-error" }}
            spellCheck={false}
            autoCapitalize="off"
            inputMode="tel"
            type="tel"
          />
        </Box>
      </Card>

      {result.kind === "reading" && <Analysis reading={result.reading} />}
      {result.kind === "short" && <ShortNumber short={result.short} />}
    </Stack>
  );
}

function Analysis({ reading }: { reading: Reading }) {
  return (
    <>
      <Card withBorder shadow="sm" radius="md" data-region={reading.region?.code ?? ""}>
        <Stack gap="sm">
          <Group gap="md" align="center" wrap="nowrap">
            {/* Read as a picture and not as the digits beside it; a number in no country has no flag to draw. */}
            <Text span className="phone-number-flag">{reading.region?.flag}</Text>
            <Box miw={0}>
              <Text className="phone-number-figure">{reading.formats[1].value}</Text>
              <Text size="sm" c="dimmed">{reading.region?.name ?? "No country of its own"}</Text>
            </Box>
          </Group>
          <Group gap="xs">
            <Badge variant="light" color={reading.valid ? "teal" : "orange"} size="sm" tt="none">
              {reading.valid ? "Valid" : "Not a valid number"}
            </Badge>
            {/* Why it failed, which is worth saying only when it did: a valid number has nothing to explain. */}
            {!reading.valid && <Marker>{reading.possibility}</Marker>}
            <Marker>{reading.type}</Marker>
          </Group>
        </Stack>
      </Card>

      {/* The same number written out and then taken apart: two readings of one thing, so they are read together. */}
      <Box className="card-columns">
        <Card withBorder shadow="sm" radius="md">
          <Stack gap="sm">
            <Title order={4}>Formats</Title>
            <FactTable rows={reading.formats} />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" radius="md">
          <Stack gap="sm">
            <Title order={4}>Parts</Title>
            <FactTable
              rows={[
                { label: "Country calling code", value: reading.callingCode },
                { label: "Region", value: reading.region ? `${reading.region.code} — ${reading.region.name}` : "" },
                { label: "National number", value: reading.nationalNumber },
                { label: "Area code", value: reading.areaCode },
                { label: "National destination code", value: reading.destinationCode },
                { label: "Carrier selection code", value: reading.carrierCode },
                { label: "Extension", value: reading.extension },
              ]}
            />
          </Stack>
        </Card>
      </Box>

      <Geography reading={reading} />
    </>
  );
}

// Where the range was issued, who was given it and what the clock says there — three maps Google publishes beside the
// numbering plans, and the only part of this page that is not a reading of the digits themselves. Only a valid number
// is asked about: a prefix that belongs to nobody yet would be answered with wherever it is heading, which is a place
// somebody typing has not arrived at. The card is absent until an answer comes back and absent when none does, a
// heading over three empty rows being a promise the maps did not keep.
function Geography({ reading }: { reading: Reading }) {
  const [found, setFound] = useState<{ number: string; place: Place }>();

  useEffect(() => {
    if (!reading.valid) return;
    const { number } = reading;
    const asked = (place: Place) => setFound({ number: number.number, place });
    place(number).then(asked, () => asked(NOWHERE));
  }, [reading.number, reading.valid]);

  // An answer is kept with the number it was asked about, because a lookup is a fetch and a parse of a whole calling
  // code's map: a slower one landing after a faster one would otherwise put one number's town under another's digits.
  const showing = reading.valid && found?.number === reading.number.number ? found.place : NOWHERE;
  const zones = showing.zones.map(zoneWithOffset).join(", ");
  if (!showing.location && !showing.network && !zones) return null;

  return (
    <Card withBorder shadow="sm" radius="md" data-place>
      <Stack gap="sm">
        <Title order={4}>Place</Title>
        <FactTable
          rows={[
            { label: "Location", value: showing.location },
            { label: showing.zones.length > 1 ? "Time zones" : "Time zone", value: zones },
            { label: "Carrier", value: showing.network },
          ]}
        />
      </Stack>
    </Card>
  );
}

// This is the whole reading for a short code, and it stands instead of the cards above rather than beside them: 000
// has no E.164, no area code and no way of being dialled from abroad, so a Formats table would be four rows of
// digits that reach nothing. The region is the picked one, three digits naming none of their own.
function ShortNumber({ short }: { short: Short }) {
  return (
    <Card withBorder shadow="sm" radius="md" data-short-number data-region={short.region.code}>
      <Stack gap="sm">
        <Group gap="md" align="center" wrap="nowrap">
          <Text span className="phone-number-flag">{short.region.flag}</Text>
          <Box miw={0}>
            <Text className="phone-number-figure">{short.digits}</Text>
            <Text size="sm" c="dimmed">Short code in {short.region.name}</Text>
          </Box>
        </Group>
        <Group gap="xs">
          {short.emergency && <Badge variant="light" color="orange" size="sm" tt="none">Emergency</Badge>}
          <Marker>{short.cost}</Marker>
          {short.carrierSpecific && <Marker>Carrier specific</Marker>}
          {short.smsService && <Marker>Takes SMS</Marker>}
        </Group>
        <Text size="sm" c="dimmed">
          Dialled inside {short.region.name} and nowhere else — a short code has no international form.
        </Text>
      </Stack>
    </Card>
  );
}

// The flag is drawn beside the label and never inside it, per the note on REGION_OPTIONS; the tick is Mantine's own,
// which a custom renderer is otherwise responsible for putting back.
function renderRegionOption({ option, checked }: ComboboxLikeRenderOptionInput<{ value: string; label: string }>) {
  const region = findRegion(option.value);
  return (
    <Group gap="xs" wrap="nowrap" flex={1}>
      {checked && <CheckIcon size={12} />}
      <Text span>{region?.flag}</Text>
      <Text size="sm">{option.label}</Text>
      <Text size="sm" c="dimmed" ml="auto">+{region?.callingCode}</Text>
    </Group>
  );
}

function Marker({ children }: { children: ReactNode }) {
  return <Badge variant="light" color="gray" size="sm" tt="none">{children}</Badge>;
}
